import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, FileText } from "lucide-react";
import { SignaturePad } from "../components/tech/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EstimatePublic() {
  const { token } = useParams();
  const [est, setEst] = useState(null);
  const [error, setError] = useState(false);
  const [mode, setMode] = useState(null); // approve | decline
  const [printedName, setPrintedName] = useState("");
  const [signature, setSignature] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/estimates/${token}`)
      .then((res) => setEst(res.data))
      .catch(() => setError(true));
  }, [token]);

  const respond = async (action) => {
    if (action === "approve" && (!printedName.trim() || !signature)) {
      return toast.error("Please sign and enter your printed name to approve.");
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/estimates/${token}/respond`, {
        action, printed_name: printedName, signature_image: signature, note,
      });
      setEst(data);
      setMode(null);
      toast.success(action === "approve" ? "Estimate approved — thank you!" : "Response sent.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit response");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <main className="pt-24 min-h-screen flex items-center justify-center px-6" data-testid="estimate-public-page">
        <p className="text-zinc-400">This estimate link is invalid or no longer available. Please call us at (404) 749-7063.</p>
      </main>
    );
  }
  if (!est) {
    return <main className="pt-24 min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></main>;
  }

  return (
    <main className="pt-20 md:pt-24 min-h-screen" data-testid="estimate-public-page">
      <div className="max-w-xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <FileText className="h-6 w-6 text-blue-500" />
          <h1 className="font-heading text-2xl font-bold text-white">Service Estimate</h1>
        </div>
        <p className="text-zinc-500 text-sm mb-8">Mason's Mobile Mechanic · For {est.name}{est.vehicle ? ` · ${est.vehicle}` : ""} · Version {est.version}</p>

        <div className="bg-[#121212] border border-zinc-800 rounded-xl p-6 mb-6">
          {est.line_items.map((it, i) => (
            <div key={i} className="flex justify-between text-sm py-2 border-b border-zinc-800/60 last:border-0">
              <span className="text-zinc-300">{it.description} {Number(it.qty) !== 1 ? `× ${it.qty}` : ""}</span>
              <span className="text-white">${(Number(it.qty) * Number(it.price)).toFixed(2)}</span>
            </div>
          ))}
          <div className="flex justify-between font-heading font-bold text-white pt-3 text-lg">
            <span>Total</span><span data-testid="estimate-public-total">${Number(est.total).toFixed(2)}</span>
          </div>
          {est.notes && <p className="text-zinc-500 text-xs mt-3">{est.notes}</p>}
        </div>

        {est.status === "Approved" && (
          <div className="bg-green-600/10 border border-green-500/40 rounded-sm p-5 text-center" data-testid="estimate-approved-state">
            <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <p className="text-green-300 font-semibold">Approved &amp; signed by {est.signature?.printed_name}</p>
            <p className="text-zinc-500 text-xs mt-1">We'll be in touch to get the work started. Thank you!</p>
          </div>
        )}
        {est.status === "Declined" && (
          <div className="bg-zinc-800/40 border border-zinc-700 rounded-sm p-5 text-center" data-testid="estimate-declined-state">
            <XCircle className="h-8 w-8 text-zinc-400 mx-auto mb-2" />
            <p className="text-zinc-300 font-semibold">Estimate declined</p>
            <p className="text-zinc-500 text-xs mt-1">Changed your mind or have questions? Call or text (404) 749-7063.</p>
          </div>
        )}

        {est.status === "Sent" && !mode && (
          <div className="flex flex-col sm:flex-row gap-3">
            <button data-testid="estimate-approve-button" onClick={() => setMode("approve")} className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-6 py-4 font-semibold">
              <CheckCircle2 className="h-5 w-5" /> Accept &amp; Sign
            </button>
            <button data-testid="estimate-decline-button" onClick={() => setMode("decline")} className="flex-1 inline-flex items-center justify-center gap-2 border border-zinc-700 hover:border-zinc-500 text-white rounded-sm px-6 py-4 font-semibold">
              <XCircle className="h-5 w-5" /> Decline
            </button>
          </div>
        )}

        {mode === "approve" && (
          <div className="bg-[#121212] border border-blue-500/30 rounded-xl p-6 space-y-4" data-testid="estimate-sign-section">
            <p className="text-zinc-400 text-sm">By signing below, I approve this estimate (version {est.version}) and authorize Mason's Mobile Mechanic to perform the work described.</p>
            <SignaturePad onChange={setSignature} />
            <input
              data-testid="estimate-printed-name"
              className="w-full bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-3 h-11 text-sm"
              placeholder="Printed full name *"
              value={printedName}
              onChange={(e) => setPrintedName(e.target.value)}
            />
            <button data-testid="estimate-submit-approval" onClick={() => respond("approve")} disabled={submitting || !signature || !printedName.trim()} className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-sm px-6 py-3.5 font-semibold">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Approve Estimate — ${Number(est.total).toFixed(2)}
            </button>
          </div>
        )}
        {mode === "decline" && (
          <div className="bg-[#121212] border border-zinc-800 rounded-xl p-6 space-y-4" data-testid="estimate-decline-section">
            <textarea
              data-testid="estimate-decline-note"
              className="w-full bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm p-3 text-sm h-24"
              placeholder="Optional: tell us why, or what you'd like changed..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <button data-testid="estimate-submit-decline" onClick={() => respond("decline")} disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 border border-zinc-700 text-white rounded-sm px-6 py-3.5 font-semibold disabled:opacity-60">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Submit — Decline Estimate
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
